import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ApiService } from '../services/api';
import { AuthService } from '../services/auth';
import { Session, Question } from '../types';
import { useSocket } from '../hooks/useSocket';

const TeacherSessionLive: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teacher, setTeacher] = useState<any>(null);
  const [responseStats, setResponseStats] = useState<any>(null);

  const { socket } = useSocket();

  // Charger les données de la session
  const loadSessionData = useCallback(async () => {
    if (!sessionId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [sessionResponse, questionsResponse] = await Promise.all([
        ApiService.getSession(sessionId),
        ApiService.getSessionQuestions(sessionId)
      ]);

      console.log('🔍 Session rechargée:', sessionResponse);
      console.log('🔍 Questions rechargées:', questionsResponse);
      
      if (sessionResponse.success) {
        setSession(sessionResponse.session);
        setCurrentQuestion(sessionResponse.session.currentQuestion); 
        setQuestions(sessionResponse.session.questions);
        const stats = calculateResponseStats(sessionResponse.session.currentQuestion);
        setResponseStats(stats); 
      } 
    } catch (err: any) {
      console.error('Erreur chargement session live:', err);
      setError(err.message || 'Erreur lors du chargement de la session');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // Fonction pour calculer les statistiques des réponses (pour tous les types de questions)
  const calculateResponseStats = (question: Question) => {
    if (!question || !question.responses || question.responses.length === 0) {
      return null;
    }

    const stats: any = {
      totalResponses: question.responses.length,
      optionCounts: {},
      correctCount: 0,
      incorrectCount: 0
    };

    // Compter les réponses selon le type de question
    question.responses.forEach(response => {
      const answer = response.answer;
      
      if (question.type === 'multiple_choice' || question.type === 'true_false' || question.type === 'investigation') {
        // Pour les QCM et Vrai/Faux, compter par option
        const optionText = question.options && question.options[parseInt(answer)] 
          ? question.options[parseInt(answer)].text 
          : `Option ${answer}`;
        
        stats.optionCounts[optionText] = (stats.optionCounts[optionText] || 0) + 1;
        
        // Compter correct/incorrect seulement si une bonne réponse est définie
        if (question.type !== 'investigation' && question.correctAnswer && question.correctAnswer !== '') {
          if (answer === question.correctAnswer) {
            stats.correctCount++;
          } else {
            stats.incorrectCount++;
          }
        }
      } else if (question.type === 'rating') {
        // Pour les questions rating, utiliser les labels ou les valeurs numériques
        const ratingValue = parseInt(answer);
        const labelText = question.ratingScale?.labels?.[ratingValue - question.ratingScale.min] || answer;
        
        stats.optionCounts[labelText] = (stats.optionCounts[labelText] || 0) + 1;
        
        // Pas de comptage correct/incorrect pour les questions rating
        stats.correctCount = undefined;
        stats.incorrectCount = undefined;
      } else if (question.type === 'open_ended') {
        // Pour les questions ouvertes, compter par réponse unique (tronquée pour l'affichage)
        const truncatedAnswer = answer.length > 50 ? answer.substring(0, 50) + '...' : answer;
        stats.optionCounts[truncatedAnswer] = (stats.optionCounts[truncatedAnswer] || 0) + 1;
        
        // Pas de comptage correct/incorrect pour les questions ouvertes
        stats.correctCount = undefined;
        stats.incorrectCount = undefined;
      }
    });

    if (question.type === 'investigation') {
      stats.correctCount = undefined;
      stats.incorrectCount = undefined;
    }

    return stats;
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadSessionData();
    
    // Charger les informations de l'enseignant
    const teacherInfo = AuthService.getTeacher();
    if (teacherInfo) {
      setTeacher(teacherInfo);
    }
  }, [sessionId, loadSessionData]);

  // Écouter les événements WebSocket
  useEffect(() => {
    if (!socket || !session || !teacher) return;

    // Rejoindre la session en tant qu'enseignant
    console.log('🔌 Enseignant rejoint la session:', session.code);
    socket.joinSession(session.code, 'teacher', teacher.id, teacher.name);

    // Écouter les nouvelles réponses
    socket.on('answer-submitted', (data) => {
      console.log('🔔 Nouvelle réponse reçue:', data);
      if (data.questionId && session) {
        // Recharger uniquement cette question depuis l'API sans recharger toute la session
        ApiService.getQuestion(data.questionId).then(response => {
          if (response.success) {
            // Mettre à jour la question spécifique
            setQuestions(prev => prev.map(q => 
              q.id === data.questionId ? response.question : q
            ));
            
            // Recalculer les stats si c'est la question active
            if (session.currentQuestion && session.currentQuestion.id === data.questionId) {
              const stats = calculateResponseStats(response.question);
              setResponseStats(stats);
              console.log('📊 Stats mises à jour pour question active:', stats);
            }
          }
        }).catch(err => console.error('Erreur rechargement question:', err));
      }
      
    });

    // Écouter l'activation de questions
    socket.on('question-activated', (data) => {
      console.log('🔔 Question activée:', data);
      setCurrentQuestion(data.question);
    });

    return () => {
      socket.off('answer-submitted');
      socket.off('question-activated');
      // Quitter la session lors du démontage
      if (session && teacher) {
        console.log('🔌 Enseignant quitte la session:', session.code);
        socket.leaveSession();
      }
    };
  }, [socket, currentQuestion, loadSessionData, session, teacher]);

  // Calculer les statistiques pour la question active
  useEffect(() => {
    if (currentQuestion) {
      const stats = calculateResponseStats(currentQuestion);
      setResponseStats(stats);
    }
  }, [currentQuestion]);

  const handleLogout = () => {
    AuthService.logoutTeacher();
    navigate('/');
  };

  // Fonction pour déterminer s'il y a une question suivante
  const hasNextQuestion = () => {
    if (!questions || questions.length === 0) return false;
    const currentActiveQuestion = questions.find(q => q.isActive);
    if (!currentActiveQuestion) return true; // Première question disponible
    const currentIndex = questions.findIndex(q => q.id === currentActiveQuestion.id);
    return currentIndex < questions.length - 1;
  };

  // Fonction pour mettre à jour le statut de la session
  const updateSessionStatus = async (status: string) => {
    if (!session) return;
    
    try {
      const response = await ApiService.updateSessionStatus(session.id, status);
      if (response.success) {
        loadSessionData();
      } else {
        setError('Erreur lors de la mise à jour du statut');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour du statut');
    }
  };

  // Fonction pour activer la question suivante
  const activateNextQuestion = async () => {
    if (!questions || questions.length === 0) {
      setError('Aucune question disponible');
      return;
    }

    // Trouver la question actuellement active
    const currentActiveQuestion = questions.find(q => q.isActive);
    console.log('🔍 Question actuellement active:', currentActiveQuestion);
    if (!currentActiveQuestion) {
      // Si aucune question n'est active, activer la première
      try {
        const response = await ApiService.activateQuestion(questions[0].id);
        if (response.success) {
          loadSessionData();
        } else {
          setError('Erreur lors de l\'activation de la question');
        }
      } catch (err: any) {
        setError(err.message || 'Erreur lors de l\'activation de la question');
      }
    } else {
      // Trouver l'index de la question active
      const currentIndex = questions.findIndex(q => q.id === currentActiveQuestion.id);
      console.log('🔍 Index de la question active:', currentIndex);
      
      if (currentIndex < questions.length - 1) {
        console.log('🔍 Activer la question suivante:', questions[currentIndex + 1].id);
        // Activer la question suivante
        try {
          const response = await ApiService.activateQuestion(questions[currentIndex + 1].id);
          console.log('🔍 Réponse de l\'activation:', response);
          if (response.success) {
            console.log('🔍 Rechargement des données de la session');
            loadSessionData();
          } else {
            setError('Erreur lors de l\'activation de la question');
          }
        } catch (err: any) {
          setError(err.message || 'Erreur lors de l\'activation de la question');
        }
      } else {
        setError('Aucune question suivante disponible');
      }
    }
  };

  if (loading) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Session Live</h1>
            <div className="header-actions">
              <Link to="/teacher/dashboard" className="btn btn-outline btn-sm">
                ← Tableau de bord
              </Link>
            </div>
          </div>
        </header>
        <main className="main-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Chargement de la session live...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Session Live</h1>
            <div className="header-actions">
              <Link to="/teacher/dashboard" className="btn btn-outline btn-sm">
                ← Tableau de bord
              </Link>
            </div>
          </div>
        </header>
        <main className="main-content">
          <div className="error-container">
            <div className="error-message">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
            </div>
            <button onClick={() => navigate('/teacher/dashboard')} className="btn btn-primary btn-sm">
              Retour au tableau de bord
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="app-container">
        <header className="header">
          <div className="header-content">
            <h1 className="logo">Session Live</h1>
            <div className="header-actions">
              <Link to="/teacher/dashboard" className="btn btn-outline btn-sm">
                ← Tableau de bord
              </Link>
            </div>
          </div>
        </header>
        <main className="main-content">
          <div className="card">
            <div className="card-body text-center">
              <p>Session non trouvée</p>
              <button onClick={() => navigate('/teacher/dashboard')} className="btn btn-primary btn-sm">
                Retour au tableau de bord
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-container">
      <header className="header">
        <div className="header-content">
          <h1 className="logo">Session Live</h1>
          <div className="header-actions">
            <div className="teacher-info">
              <span>👤 {teacher?.name}</span>
            </div>
            <button onClick={handleLogout} className="btn btn-outline btn-sm">
              <i className="fas fa-sign-out-alt"></i>
              Déconnexion
            </button>
            <Link to="/teacher/dashboard" className="btn btn-outline btn-sm">
              ← Tableau de bord
            </Link>
            <Link to={`/teacher/sessions/${session.id}`} className="btn btn-outline btn-sm">
              ← Gestion
            </Link>
          </div>
        </div>
      </header>

      <main className="main-content">
        {/* Informations de la session */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Session Live</h2>
            <div className="flex gap-2 align-center">
              <span className={`status-badge ${session.status}`}>
                {session.status === 'active' && '🔴 LIVE'}
                {session.status === 'paused' && '⏸️ PAUSE'}
                {session.status === 'ended' && '⏹️ TERMINÉE'}
              </span>
              <span className="status-badge status-info">
                👥 {session.participantsCount} participants
              </span>
              {questions.length > 0 && hasNextQuestion() && (
                <button
                  onClick={activateNextQuestion}
                  className="btn btn-primary btn-sm"
                  disabled={session.status !== 'active'}
                >
                  ➡️ Question Suivante
                </button>
              )}
              
              {/* Boutons quand il n'y a plus de question suivante */}
              {questions.length > 0 && !hasNextQuestion() && session.status === 'active' && (
                <div className="flex gap-1">
                  <button
                    onClick={() => updateSessionStatus('paused')}
                    className="btn btn-warning btn-sm"
                  >
                    ⏸️ Pause
                  </button>
                  <button
                    onClick={() => updateSessionStatus('ended')}
                    className="btn btn-danger btn-sm"
                  >
                    ⏹️ Terminer
                  </button>
                  <Link
                    to={`/teacher/sessions/${session.id}`}
                    className="btn btn-outline btn-sm"
                  >
                    ⚙️ Gestion
                  </Link>
                </div>
              )}
              
              {/* Bouton Reprendre quand la session est en pause */}
              {session.status === 'paused' && (
                <button
                  onClick={() => updateSessionStatus('active')}
                  className="btn btn-success btn-sm"
                >
                  ▶️ Reprendre
                </button>
              )}
            </div>
          </div>
          
          <div className="card-body">
            <div className="grid grid-2">
              <div>
                <h3>Informations de la Session</h3>
                <div className="session-info">
                  <p><strong>Nom:</strong> {session.name}</p>
                  <p><strong>Code:</strong> <code>{session.code}</code></p>
                  <p><strong>Statut:</strong> {session.status}</p>
                  <p><strong>Participants:</strong> {session.participantsCount}</p>
                  <p><strong>Questions:</strong> {questions.length}</p>
                </div>
              </div>
              
              <div>
                <h3>Question Actuelle</h3>
                {currentQuestion ? (
                  <div className="current-question">
                    <h4>{currentQuestion.title}</h4>
                    <p>{currentQuestion.content}</p>
                    <span className="question-type-badge">
                      {currentQuestion.type === 'multiple_choice' && 'QCM'}
                      {currentQuestion.type === 'investigation' && "Investigation"}
                      {currentQuestion.type === 'open_ended' && 'Question ouverte'}
                      {currentQuestion.type === 'true_false' && 'Vrai/Faux'}
                      {currentQuestion.type === 'rating' && 'Évaluation'}
                    </span>
                    <div className="question-stats">
                      <span>📊 {currentQuestion.responses?.length || 0} réponses</span>
                    </div>
                  </div>
                ) : (
                  <p>Aucune question active</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Statistiques de la question active */}
        {currentQuestion && responseStats && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">📊 Statistiques en Temps Réel</h3>
              <span className="status-badge status-active">
                {currentQuestion.title}
              </span>
            </div>
            <div className="card-body">
              <div className="stats-summary">
                <div className="stat-item">
                  <span className="stat-number">{responseStats.totalResponses}</span>
                  <span className="stat-label">Total réponses</span>
                </div>
                {responseStats.correctCount !== undefined && session.status === 'ended' && (
                  <div className="stat-item">
                    <span className="stat-number" style={{ color: '#28a745' }}>
                      {responseStats.correctCount}
                    </span>
                    <span className="stat-label">Correctes</span>
                  </div>
                )}
                {responseStats.incorrectCount !== undefined && session.status === 'ended' && (
                  <div className="stat-item">
                    <span className="stat-number" style={{ color: '#dc3545' }}>
                      {responseStats.incorrectCount}
                    </span>
                    <span className="stat-label">Incorrectes</span>
                  </div>
                )}
              </div>

              {/* Histogramme des réponses */}
              <div className="histogram-container">
                <h4>Distribution des Réponses</h4>
                <div className="histogram">
                  {Object.entries(responseStats.optionCounts).map(([option, count]) => {
                    const percentage = (count as number / responseStats.totalResponses) * 100;
                    return (
                      <div key={option} className="histogram-bar">
                        <div 
                          className="bar-label" 
                          style={{
                            wordBreak: currentQuestion.type === 'open_ended' ? 'break-word' : 'normal',
                            overflow: currentQuestion.type === 'open_ended' ? 'hidden' : 'visible',
                            maxWidth: currentQuestion.type === 'open_ended' ? '300px' : 'none',
                            fontSize: currentQuestion.type === 'open_ended' ? '0.9em' : '1em',
                            lineHeight: currentQuestion.type === 'open_ended' ? '1.3' : 'normal'
                          }}
                          title={currentQuestion.type === 'open_ended' ? option : undefined}
                        >
                          {option}
                        </div>
                        <div className="bar-container">
                          <div 
                            className="bar-fill"
                            style={{
                              width: `${percentage}%`,
                              background: currentQuestion.type === 'rating' ? '#007bff' : 
                                         currentQuestion.type === 'open_ended' ? '#17a2b8' :
                                         option === currentQuestion.correctAnswer ? '#28a745' : '#6c757d'
                            }}
                          />
                        </div>
                        <div className="bar-value">
                          {`${count} (${percentage.toFixed(1)}%)`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Messages si aucune question active */}
        {!currentQuestion && (
          <div className="card">
            <div className="card-body text-center">
              <h3>Aucune Question Active</h3>
              <p>Activez une question depuis la gestion de session pour voir les réponses en direct.</p>
              <Link 
                to={`/teacher/sessions/${session.id}`}
                className="btn btn-primary btn-sm"
              >
                Gérer la Session
              </Link>
            </div>
          </div>
        )}
      </main>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .live-responses {
            max-height: 400px;
            overflow-y: auto;
          }
          
          .stats-summary {
            display: flex;
            gap: 2rem;
            margin-bottom: 2rem;
            padding: 1rem;
            background: #f8f9fa;
            border-radius: 8px;
          }
          
          .stat-item {
            text-align: center;
          }
          
          .stat-number {
            display: block;
            font-size: 2rem;
            font-weight: bold;
            color: #007bff;
          }
          
          .stat-label {
            font-size: 0.9em;
            color: #6c757d;
          }
          
          .histogram-container {
            margin-top: 1rem;
          }
          
          .histogram {
            margin-top: 1rem;
          }
          
          .histogram-bar {
            display: flex;
            align-items: center;
            margin-bottom: 0.5rem;
            gap: 1rem;
          }
          
          .bar-label {
            min-width: 120px;
            font-weight: 500;
          }
          
          .bar-container {
            flex: 1;
            height: 20px;
            background: #e9ecef;
            border-radius: 10px;
            overflow: hidden;
          }
          
          .bar-fill {
            height: 100%;
            transition: width 0.5s ease;
          }
          
          .bar-value {
            min-width: 80px;
            text-align: right;
            font-size: 0.9em;
            color: #6c757d;
          }
          
          .current-question {
            padding: 1rem;
            background: #f8f9fa;
            border-radius: 8px;
          }
          
          .question-stats {
            margin-top: 1rem;
            padding-top: 1rem;
            border-top: 1px solid #e9ecef;
          }
        `
      }} />
    </div>
  );
};

export default TeacherSessionLive;
